/** Drive v3 calls Finance Buddy needs: folders, listing, download, move. */
import { api, GoogleAPIError } from './api'

const DRIVE = 'https://www.googleapis.com/drive/v3/files'
const UPLOAD = 'https://www.googleapis.com/upload/drive/v3/files'
export const FOLDER = 'application/vnd.google-apps.folder'
export const SPREADSHEET = 'application/vnd.google-apps.spreadsheet'
export const ROOT_FOLDER_NAME = 'Finance Buddy'

export interface DriveFile {
  id: string
  name: string
  mimeType: string
  size?: string
  webViewLink?: string
  parents?: string[]
  /** Set by Drive for uploaded (non-Google) files; equal bytes give equal checksums. */
  md5Checksum?: string
}

const FIELDS = 'id,name,mimeType,size,webViewLink,parents,md5Checksum'

/** Drive query strings quote with single quotes; escape them in names. */
function q(value: string): string {
  return `'${value.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`
}

async function list(query: string, spaces = 'drive'): Promise<DriveFile[]> {
  const files: DriveFile[] = []
  let pageToken: string | undefined
  do {
    const params = new URLSearchParams({ q: query, fields: `nextPageToken,files(${FIELDS})`, pageSize: '1000', spaces })
    if (pageToken) params.set('pageToken', pageToken)
    const page = await api.get<{ files: DriveFile[]; nextPageToken?: string }>(`${DRIVE}?${params}`)
    files.push(...page.files)
    pageToken = page.nextPageToken
  } while (pageToken)
  return files
}

export async function findChild(parentId: string, name: string, mimeType?: string): Promise<DriveFile | undefined> {
  const clauses = [`${q(parentId)} in parents`, `name = ${q(name)}`, 'trashed = false']
  if (mimeType) clauses.push(`mimeType = ${q(mimeType)}`)
  return (await list(clauses.join(' and ')))[0]
}

export async function create(parentId: string, name: string, mimeType: string): Promise<DriveFile> {
  return api.post<DriveFile>(`${DRIVE}?fields=${FIELDS}`, { name, mimeType, parents: [parentId] })
}

export async function findOrCreateFolder(parentId: string, name: string): Promise<DriveFile> {
  return (await findChild(parentId, name, FOLDER)) ?? create(parentId, name, FOLDER)
}

/** Resolves a folder path under `rootId`, creating what's missing. Cached per run. */
export class FolderCache {
  private cache = new Map<string, Promise<string>>()
  constructor(private rootId: string) {}

  resolve(path: string[]): Promise<string> {
    if (path.length === 0) return Promise.resolve(this.rootId)
    const key = path.join('/')
    let hit = this.cache.get(key)
    if (!hit) {
      hit = this.resolve(path.slice(0, -1)).then(async (parent) => (await findOrCreateFolder(parent, path.at(-1)!)).id)
      // A failed lookup must not poison the cache for the rest of the run.
      hit.catch(() => this.cache.delete(key))
      this.cache.set(key, hit)
    }
    return hit
  }
}

/** Files (not folders) directly inside `folderId`. */
export async function listFiles(folderId: string): Promise<DriveFile[]> {
  return list(`${q(folderId)} in parents and trashed = false and mimeType != ${q(FOLDER)}`)
}

export async function fileInfo(fileId: string): Promise<DriveFile> {
  return api.get<DriveFile>(`${DRIVE}/${fileId}?fields=${FIELDS}`)
}

export async function download(fileId: string): Promise<Blob> {
  return api.download(`${DRIVE}/${fileId}?alt=media`)
}

/** Google-native files have no bytes of their own; export them first. */
export async function exportAs(fileId: string, mimeType: string): Promise<Blob> {
  return api.download(`${DRIVE}/${fileId}/export?mimeType=${encodeURIComponent(mimeType)}`)
}

/** Drive's checksum for one file, or undefined for Google-native files. */
export async function checksum(fileId: string): Promise<string | undefined> {
  return (await api.get<{ md5Checksum?: string }>(`${DRIVE}/${fileId}?fields=md5Checksum`)).md5Checksum
}

/** False when the file has been deleted, trashed, or is no longer visible to this account. */
export async function stillThere(fileId: string): Promise<boolean> {
  try {
    return !(await api.get<{ trashed?: boolean }>(`${DRIVE}/${fileId}?fields=trashed`)).trashed
  } catch (error) {
    if (error instanceof GoogleAPIError && error.status === 404) return false
    throw error
  }
}

export async function moveAndRename(file: DriveFile, toFolderId: string, name: string): Promise<DriveFile> {
  const params = new URLSearchParams({ fields: FIELDS, addParents: toFolderId })
  const from = (file.parents ?? []).filter((p) => p !== toFolderId)
  if (from.length) params.set('removeParents', from.join(','))
  return api.patch<DriveFile>(`${DRIVE}/${file.id}?${params}`, { name })
}

/** A name not already taken in the folder: `x.pdf`, then `x (2).pdf`, … */
export async function freeName(folderId: string, name: string, selfId: string): Promise<string> {
  const dot = name.lastIndexOf('.')
  const [stem, ext] = dot > 0 ? [name.slice(0, dot), name.slice(dot)] : [name, '']
  for (let n = 1; n < 100; n++) {
    const candidate = n === 1 ? name : `${stem} (${n})${ext}`
    const clash = await findChild(folderId, candidate)
    if (!clash || clash.id === selfId) return candidate
  }
  return `${stem} (${Date.now()})${ext}`
}

/** The top-level `Finance Buddy` folder in My Drive, created on first use. */
export async function rootFolder(): Promise<DriveFile> {
  return findOrCreateFolder('root', ROOT_FOLDER_NAME)
}

// --- appDataFolder: hidden per-app storage -------------------------------

/** Drive's multipart upload is for files up to 5 MB; larger ones use a resumable session. */
const MULTIPART_LIMIT = 5 * 1024 * 1024

/** Uploads a file from the page into `parentId`, bytes unchanged. */
export async function uploadFile(parentId: string, file: File): Promise<DriveFile> {
  const metadata = { name: file.name, parents: [parentId] }
  const type = file.type || 'application/octet-stream'
  if (file.size <= MULTIPART_LIMIT) {
    const boundary = `fb${Math.random().toString(36).slice(2)}`
    const body = new Blob([
      `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n${JSON.stringify(metadata)}\r\n`,
      `--${boundary}\r\nContent-Type: ${type}\r\n\r\n`,
      file,
      `\r\n--${boundary}--`,
    ])
    return api.upload(`${UPLOAD}?uploadType=multipart&fields=${FIELDS}`, `multipart/related; boundary=${boundary}`, body)
  }
  const session = await api.startUpload(`${UPLOAD}?uploadType=resumable&fields=${FIELDS}`, metadata)
  return api.uploadWith('PUT', session, type, file)
}

export const DOCUMENT = 'application/vnd.google-apps.document'
/** The Google Doc the app writes its recommendations to, in the root folder. */
export const RECOMMENDATIONS_DOC = 'Recommendations'

/**
 * Writes HTML into the Google Doc `name` in `parentId`, converting it on
 * upload. An existing Doc keeps its id, so links to it stay good.
 */
export async function writeDoc(parentId: string, name: string, html: string): Promise<DriveFile> {
  const existing = await findChild(parentId, name, DOCUMENT)
  const content = new Blob([html], { type: 'text/html' })
  if (existing) return api.uploadWith('PATCH', `${UPLOAD}/${existing.id}?uploadType=media&fields=${FIELDS}`, 'text/html', content)
  const boundary = `fb${Math.random().toString(36).slice(2)}`
  const body = new Blob([
    `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n${JSON.stringify({ name, parents: [parentId], mimeType: DOCUMENT })}\r\n`,
    `--${boundary}\r\nContent-Type: text/html; charset=UTF-8\r\n\r\n`,
    content,
    `\r\n--${boundary}--`,
  ])
  return api.upload(`${UPLOAD}?uploadType=multipart&fields=${FIELDS}`, `multipart/related; boundary=${boundary}`, body)
}

/** The Doc, if it has been written. */
export async function findDoc(parentId: string, name: string): Promise<DriveFile | undefined> {
  return findChild(parentId, name, DOCUMENT)
}

export async function appDataRead(name: string): Promise<string | undefined> {
  const [file] = await list(`name = ${q(name)} and ${q('appDataFolder')} in parents`, 'appDataFolder')
  if (!file) return undefined
  return (await download(file.id)).text()
}

export async function appDataWrite(name: string, content: string): Promise<void> {
  const [existing] = await list(`name = ${q(name)} and ${q('appDataFolder')} in parents`, 'appDataFolder')
  const boundary = `fb${Math.random().toString(36).slice(2)}`
  const metadata = existing ? {} : { name, parents: ['appDataFolder'] }
  const body =
    `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n${JSON.stringify(metadata)}\r\n` +
    `--${boundary}\r\nContent-Type: application/json\r\n\r\n${content}\r\n--${boundary}--`
  const contentType = `multipart/related; boundary=${boundary}`
  if (existing) {
    await api.uploadWith('PATCH', `${UPLOAD}/${existing.id}?uploadType=multipart`, contentType, body)
  } else {
    await api.upload(`${UPLOAD}?uploadType=multipart`, contentType, body)
  }
}

export async function appDataDelete(name: string): Promise<void> {
  const files = await list(`name = ${q(name)} and ${q('appDataFolder')} in parents`, 'appDataFolder')
  for (const f of files) await api.delete(`${DRIVE}/${f.id}`)
}
